import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// =========================================================================
// Brand (client) selector — fork-owned (zone C).
//
// OEM+ ships one product skinned for one OEM identity at a time. A brand
// controls five things and nothing else: the accent token families, the logo
// in the lockup, the wordmark ink, the favicon, and the machine livery (pink
// Meridian paint, yellow Komatsu paint). Fleet data, models, mines, personas
// and routes are identical across brands.
//
// The default is **Meridian**, a fictional OEM with pink machines, so the demo
// is safe for any audience — including one OEM looking at another OEM's work.
// Komatsu is opt-in from Settings → Appearance → OEM identity, for
// Komatsu-only sessions (2026-09-25, Jay's review).
//
// The registry is data-driven on purpose. Adding a third client (Sandvik, say)
// is one BRANDS entry + three SVGs under public/brands/<id>/ + two token
// blocks in styles/brands.css. Nothing else in the app needs to change.
//
// The provider writes `document.documentElement.dataset.brand`, which is what
// the `:root[data-brand="…"][data-theme="…"]` blocks in styles/brands.css key
// off. See that file for the cascade reasoning.
// =========================================================================

export type BrandId = 'komatsu' | 'meridian';

export type Brand = {
  /** Registry key; also the `public/brands/<id>/` folder and the DOM attribute value. */
  id: BrandId;
  /** Legal-ish client name, used as the logo `title` / alt text. */
  client: string;
  /** Short label for the lockup and the brand menu. */
  lockup: string;
  /** Logo for light surfaces. */
  logo: string;
  /** Logo for dark surfaces. */
  logoReversed: string;
  /** Swapped into `<link rel="icon">` on selection. */
  favicon: string;
  /** Swatch colour for the settings cards and the menu dot (light-theme accent). */
  accentHex: string;
  /**
   * Machine livery swatch for the settings cards — the same value as this
   * brand's `--brand-secondary` in styles/brands.css (light theme). Hard-coded
   * here because the token is keyed on `[data-brand]`, so the card for the
   * brand that is NOT selected cannot read it from CSS.
   */
  machineHex: string;
  /**
   * Whether the lockup reads "AiRis ProjeX OEM+ for <logo>". True for a real
   * client; false for the fictional Meridian, whose mark sits beside the
   * wordmark without "for" so the demo never claims a client.
   */
  lockupFor: boolean;
  /** Shown under the settings card so the trademark position travels with the UI. */
  trademark: string;
};

export const BRANDS: Record<BrandId, Brand> = {
  komatsu: {
    id: 'komatsu',
    client: 'Komatsu Ltd',
    lockup: 'Komatsu',
    logo: '/brands/komatsu/logo.svg',
    logoReversed: '/brands/komatsu/logo-reversed.svg',
    favicon: '/brands/komatsu/favicon.svg',
    accentHex: '#140a9a',
    machineHex: '#ffcc00',
    lockupFor: true,
    trademark: 'Komatsu marks are the property of Komatsu Ltd. Demo use only.',
  },
  meridian: {
    id: 'meridian',
    client: 'Meridian Equipment Pty Ltd',
    lockup: 'Meridian',
    logo: '/brands/meridian/logo.svg',
    logoReversed: '/brands/meridian/logo-reversed.svg',
    favicon: '/brands/meridian/favicon.svg',
    accentHex: '#a3123f',
    machineHex: '#c42c7a',
    lockupFor: false,
    trademark: 'Meridian Equipment is a fictional OEM — safe for any audience.',
  },
};

export const BRAND_IDS: BrandId[] = ['meridian', 'komatsu'];

/** localStorage key. Namespaced so it never collides with foundation keys. */
export const BRAND_STORAGE_KEY = 'oem-plus:brand';

/**
 * Module-level default. Deliberately a constant rather than a storage read:
 * the snapshot renderer and any SSR pass must produce Meridian markup
 * deterministically, and a first paint that flips brand would flash.
 * Stored preference is applied in an effect, after mount.
 */
export const DEFAULT_BRAND: BrandId = 'meridian';

function isBrandId(value: unknown): value is BrandId {
  return value === 'meridian' || value === 'komatsu';
}

/** Storage read that survives private mode, blocked site data and SSR. */
function readStoredBrand(): BrandId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BRAND_STORAGE_KEY);
    return isBrandId(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredBrand(id: BrandId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BRAND_STORAGE_KEY, id);
  } catch {
    // Private window / blocked storage — the session still works, it just
    // won't remember the choice.
  }
}

/** Point the existing `<link rel="icon">` at this brand's favicon. */
function applyFavicon(href: string): void {
  if (typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/svg+xml';
  link.href = href;
}

type BrandContextValue = {
  brand: Brand;
  brandId: BrandId;
  brands: Brand[];
  setBrand: (id: BrandId) => void;
};

const BrandContext = createContext<BrandContextValue | null>(null);

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brandId, setBrandId] = useState<BrandId>(DEFAULT_BRAND);

  // Rehydrate from storage after mount so first paint stays deterministic.
  useEffect(() => {
    const stored = readStoredBrand();
    if (stored && stored !== brandId) setBrandId(stored);
    // Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.brand = brandId;
    applyFavicon(BRANDS[brandId].favicon);
  }, [brandId]);

  const setBrand = useCallback((id: BrandId) => {
    setBrandId(id);
    writeStoredBrand(id);
  }, []);

  const value = useMemo<BrandContextValue>(
    () => ({
      brand: BRANDS[brandId],
      brandId,
      brands: BRAND_IDS.map((id) => BRANDS[id]),
      setBrand,
    }),
    [brandId, setBrand],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/**
 * Value served when no provider is mounted: the default brand, read-only.
 *
 * Unlike the other contexts in this app, `useBrand` does NOT throw outside its
 * provider. A brand is a skin over a fixed default, and two callers render
 * outside the app tree on purpose:
 *
 *   - `scripts/parity/render-snapshots.mjs` mounts each scene under the
 *     foundation `Providers` only, so a throwing hook would break every
 *     snapshot target that reaches a `<BrandMark/>`.
 *   - any future static or server render of an auth scene.
 *
 * Both should produce the deterministic Meridian skin, which is exactly what
 * `DEFAULT_BRAND` already guarantees. `setBrand` is inert here and warns in
 * development, so a genuinely missing provider still surfaces in the console
 * the first time someone tries to switch.
 */
const FALLBACK_BRAND: BrandContextValue = {
  brand: BRANDS[DEFAULT_BRAND],
  brandId: DEFAULT_BRAND,
  brands: BRAND_IDS.map((id) => BRANDS[id]),
  setBrand: () => {
    if (import.meta.env?.DEV) {
      console.warn(
        '[oem] setBrand() called outside <BrandProvider> — the brand is fixed to ' +
          `"${DEFAULT_BRAND}" in this tree.`,
      );
    }
  },
};

export function useBrand(): BrandContextValue {
  return useContext(BrandContext) ?? FALLBACK_BRAND;
}
